/**
 * Daily Bonus Service for Poker Reborn
 *
 * Handles communication with the daily_bonus service (GLID "daily_bonus").
 * Manages streak status, bonus claims, and countdown state.
 *
 * Follows the InboxService singleton pattern:
 * - Listens to NetworkService messages from GLID "daily_bonus"
 * - Emits typed events via EventEmitter<DailyBonusServiceEvents>
 * - Server returns full state on status, claim result on claim
 *
 * IMPORTANT: Player must actively claim each day to keep the streak.
 * Missing a claim before streak_expires_at resets the streak to Day 1.
 *
 * @see tasks/active/T37_DAILY_BONUS_PLAN.md - Feature design
 * @see tasks/active/T37_DAILY_BONUS_IMPL.md - Implementation details
 */

import { EventEmitter } from './EventEmitter';
import { networkService } from './NetworkService';
import { GLIDS } from '@/config/network';
import { log } from '@/utils/log';

// =============================================================================
// TYPES
// =============================================================================

/** A single monthly milestone threshold */
export interface MonthlyMilestone {
  /** Days required (7, 14, 21, or 30) */
  days: number;
  /** Chip reward for this milestone */
  reward: number;
  /** True if already achieved this calendar month */
  achieved: boolean;
}

/** Monthly milestone progress within current calendar month */
export interface MonthlyProgress {
  /** Total daily bonus claims this calendar month */
  claimCount: number;
  /** 4 milestone thresholds with progress */
  milestones: MonthlyMilestone[];
}

/** Daily bonus status from server */
export interface DailyBonusStatus {
  /** True if bonus can be claimed now */
  available: boolean;
  /** Current streak day (0-7), 0 = never claimed */
  streakDay: number;
  /** Chips for next claim */
  nextReward: number;
  /** ISO datetime when next claim available (null if available now) */
  nextClaimAt: string | null;
  /** ISO datetime when streak resets if not claimed */
  streakExpiresAt: string | null;
  /** Lifetime claim count */
  totalClaimed: number;
  /** Lifetime chips earned from daily bonus */
  totalChipsEarned: number;
  /** Reward schedule: index 0 = Day 1, index 6 = Day 7 */
  rewards: number[];
  /** Monthly milestone progress */
  monthly: MonthlyProgress;
}

/** Milestone crossed during a claim (null if none) */
export interface ClaimedMilestone {
  /** Milestone threshold (7, 14, 21, or 30) */
  days: number;
  /** Milestone chip reward */
  reward: number;
  /** Badge ID for day-30 milestone, null otherwise */
  badge: string | null;
}

/** Successful claim response */
export interface DailyBonusClaimed {
  /** Chips awarded (daily + milestone if crossed) */
  reward: number;
  /** New streak day after claiming */
  streakDay: number;
  /** ISO datetime when next claim is available */
  nextClaimAt: string;
  /** ISO datetime when streak resets if not claimed */
  streakExpiresAt: string;
  /** True if streak was reset due to expiry before this claim */
  streakReset: boolean;
  /** Monthly milestone crossed on this claim (null if none) */
  milestone: ClaimedMilestone | null;
}

/** Events emitted by DailyBonusService */
export interface DailyBonusServiceEvents {
  /** Status received (response to # status) */
  statusUpdated: DailyBonusStatus;
  /** Bonus claimed successfully */
  claimed: DailyBonusClaimed;
  /** Error from daily bonus service */
  error: { code: string; message: string };
}

// =============================================================================
// DAILY BONUS SERVICE
// =============================================================================

class DailyBonusService extends EventEmitter<DailyBonusServiceEvents> {
  private static instance: DailyBonusService | null = null;
  private lastStatus: DailyBonusStatus | null = null;

  private constructor() {
    super();
    this.setupNetworkHandlers();
  }

  /** Get singleton instance */
  static getInstance(): DailyBonusService {
    if (!DailyBonusService.instance) {
      DailyBonusService.instance = new DailyBonusService();
    }
    return DailyBonusService.instance;
  }

  // ===========================================================================
  // PUBLIC API - Commands
  // ===========================================================================

  /**
   * Request current daily bonus status.
   * Call after authentication completes.
   * Triggers 'statusUpdated' event.
   */
  requestStatus(): void {
    if (!networkService.isConnected()) {
      log.dailyBonus.warning`Cannot request status: not connected`;
      return;
    }
    log.dailyBonus.info`Requesting daily bonus status...`;
    networkService.sendCommand(GLIDS.DAILY_BONUS, '# status');
  }

  /**
   * Claim the daily bonus.
   * Triggers 'claimed' event on success, 'error' on failure.
   */
  claim(): void {
    if (!networkService.isConnected()) {
      log.dailyBonus.warning`Cannot claim bonus: not connected`;
      return;
    }
    log.dailyBonus.info`Claiming daily bonus...`;
    networkService.sendCommand(GLIDS.DAILY_BONUS, '# claim');
  }

  // ===========================================================================
  // PUBLIC API - State
  // ===========================================================================

  /** Get last known status (null before first request) */
  getLastStatus(): DailyBonusStatus | null {
    return this.lastStatus;
  }

  // ===========================================================================
  // PRIVATE - Network Handlers
  // ===========================================================================

  private setupNetworkHandlers(): void {
    networkService.on('message', ({ from, body }) => {
      if (from !== GLIDS.DAILY_BONUS) return;

      log.dailyBonus.debug`Daily bonus message received: ${body}`;

      try {
        const message = JSON.parse(body) as Record<string, unknown>;

        switch (message.a) {
          case 'daily_bonus_status':
            this.handleStatusResponse(message);
            break;

          case 'daily_bonus_claimed':
            this.handleClaimResponse(message);
            break;

          case 'daily_bonus_error':
            this.handleErrorResponse(message);
            break;

          default:
            log.dailyBonus.warning`Unknown daily bonus action: ${message.a}`;
        }
      } catch {
        log.dailyBonus.warning`Failed to parse daily bonus message: ${body}`;
      }
    });
  }

  private handleStatusResponse(message: Record<string, unknown>): void {
    const daily = message.daily as Record<string, unknown> | undefined;
    if (!daily) {
      log.dailyBonus.warning`Missing daily field in status response`;
      return;
    }

    const monthlyRaw = message.monthly as Record<string, unknown> | undefined;
    const milestoneArray = (monthlyRaw?.milestones as Array<Record<string, unknown>> | undefined) ?? [];

    const status: DailyBonusStatus = {
      available: Boolean(daily.available),
      streakDay: Number(daily.streak_day ?? 0),
      nextReward: Number(daily.next_reward ?? 0),
      nextClaimAt: (daily.next_claim_at as string) ?? null,
      streakExpiresAt: (daily.streak_expires_at as string) ?? null,
      totalClaimed: Number(daily.total_claimed ?? 0),
      totalChipsEarned: Number(daily.total_chips_earned ?? 0),
      rewards: Array.isArray(daily.rewards) ? (daily.rewards as number[]) : [],
      monthly: {
        claimCount: Number(monthlyRaw?.claim_count ?? 0),
        milestones: milestoneArray.map((m) => ({
          days: Number(m.days),
          reward: Number(m.reward),
          achieved: Boolean(m.achieved),
        })),
      },
    };

    this.lastStatus = status;
    this.emit('statusUpdated', status);

    log.dailyBonus.info`Status: day ${status.streakDay}, available=${String(status.available)}, next=${status.nextReward} chips, monthly=${String(status.monthly.claimCount)}/30`;
  }

  private handleClaimResponse(message: Record<string, unknown>): void {
    const rawMilestone = message.milestone as Record<string, unknown> | null | undefined;

    const claimedMilestone: ClaimedMilestone | null = rawMilestone
      ? {
          days: Number(rawMilestone.days),
          reward: Number(rawMilestone.reward),
          badge: (rawMilestone.badge as string | null) ?? null,
        }
      : null;

    const claimed: DailyBonusClaimed = {
      reward: Number(message.reward ?? 0),
      streakDay: Number(message.streak_day ?? 0),
      nextClaimAt: String(message.next_claim_at ?? ''),
      streakExpiresAt: String(message.streak_expires_at ?? ''),
      streakReset: Boolean(message.streak_reset),
      milestone: claimedMilestone,
    };

    // Update cached status to reflect post-claim state
    if (this.lastStatus) {
      const updatedMonthly = { ...this.lastStatus.monthly };
      updatedMonthly.claimCount = updatedMonthly.claimCount + 1;

      if (claimedMilestone) {
        updatedMonthly.milestones = updatedMonthly.milestones.map((m) =>
          m.days === claimedMilestone.days ? { ...m, achieved: true } : m,
        );
      }

      this.lastStatus = {
        ...this.lastStatus,
        available: false,
        streakDay: claimed.streakDay,
        nextClaimAt: claimed.nextClaimAt,
        streakExpiresAt: claimed.streakExpiresAt,
        monthly: updatedMonthly,
      };
    }

    this.emit('claimed', claimed);

    // Emit statusUpdated so lobby badge disappears immediately
    if (this.lastStatus) {
      this.emit('statusUpdated', this.lastStatus);
    }

    const milestoneLog = claimedMilestone
      ? `, milestone ${String(claimedMilestone.days)}-day +${String(claimedMilestone.reward)}`
      : '';
    log.dailyBonus.info`Claimed! +${claimed.reward} chips, streak day ${claimed.streakDay}${claimed.streakReset ? ' (streak was reset)' : ''}${milestoneLog}`;
  }

  private handleErrorResponse(message: Record<string, unknown>): void {
    const errorCode = String(message.code ?? 'unknown');
    const errorMessage = String(message.message ?? 'Unknown error');

    log.dailyBonus.warning`Daily bonus error: ${errorCode} - ${errorMessage}`;
    this.emit('error', { code: errorCode, message: errorMessage });
  }
}

/** Singleton daily bonus service instance */
export const dailyBonusService = DailyBonusService.getInstance();
